import { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { api, apiError } from "@/lib/api";
import { useNotifications } from "@/hooks/useNotifications";
import { Header } from "@/components/fleet/Header";
import { Hero } from "@/components/fleet/Hero";
import { LiveBoard } from "@/components/fleet/LiveBoard";
import { Footer } from "@/components/fleet/Footer";
import { StickyCta } from "@/components/fleet/StickyCta";
import { ExpressRideModal } from "@/components/fleet/ExpressRideModal";
import { BookDialog } from "@/components/fleet/BookDialog";
import { InviteUnlockDialog } from "@/components/fleet/InviteUnlockDialog";
import { ShareRideDialog } from "@/components/fleet/ShareRideDialog";
import { MyRidesSheet } from "@/components/fleet/MyRidesSheet";
import { LegalSheet } from "@/components/fleet/LegalSheet";
import { ProfileSheet } from "@/components/fleet/ProfileSheet";
import { NotificationsSheet } from "@/components/fleet/NotificationsSheet";
import { ConciergeSheet } from "@/components/fleet/ConciergeSheet";
import ConciergeRequestPage from "@/pages/ConciergeRequestPage";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/fleet/AuthDialog";
import { AdminSheet } from "@/components/fleet/AdminSheet";
import { usePaymentReturn } from "@/hooks/usePaymentReturn";
import { enablePush } from "@/lib/push";
import { useRealtime } from "@/lib/realtime";
import { useLang } from "@/lib/i18n";
import { ResetPasswordDialog } from "@/components/fleet/ResetPasswordDialog";
import { InstallBanner } from "@/components/fleet/InstallBanner";
import { SplashScreen } from "@/components/fleet/SplashScreen";

const params = new URLSearchParams(window.location.search);
let confirmHandled = false;

function DriverApp() {
  const [panel, setPanel] = useState(null);
  const [legalSection, setLegalSection] = useState("tc");
  const [booking, setBooking] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [stats, setStats] = useState(null);
  const [online, setOnline] = useState(0);
  const lang = useLang();
  const [vehicle, setVehicle] = useState("");
  const [hub, setHub] = useState("");
  const [service, setService] = useState("");
  const [q, setQ] = useState("");
  const [profile, setProfile] = useState(null);
  const [inviteCode, setInviteCode] = useState(params.get("invite"));
  const [shareCode, setShareCode] = useState(params.get("ride"));
  const [resetToken, setResetToken] = useState(params.get("reset"));
  const searchRef = useRef(null);
  const verified = !!profile?.verified;

  const openRide = useCallback((code) => {
    setPanel(null);
    setShareCode(code);
  }, []);
  const { user, logout } = useAuth();

  useEffect(() => {
    if (user) {
      api.get("/auth/me").catch(() => {});
    }
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const loggedIn = !!user;
  const notifs = useNotifications(openRide, loggedIn);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  const loadProfile = useCallback(() => api.get("/profile").then((r) => setProfile(r.data)).catch(() => {}), []);
  const close = () => setPanel(null);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }
    loadProfile();
    if (params.get("panel") === "myrides") {
      setPanel("myrides");
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (window.Notification?.permission === "granted") enablePush().catch(() => {});
  }, [user, loadProfile]);

  usePaymentReturn(refresh);
  useRealtime(refresh, setOnline);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [refreshKey]);

  const requireLicense = (action) => {
    if (!loggedIn) {
      toast.info("Accedi o registrati per continuare");
      return setPanel("auth");
    }
    return action();
  };

  const openCreate = () => setPanel("create");

  useEffect(() => {
    const token = params.get("confirm_ride");
    if (!token || confirmHandled) {
      return;
    }
    confirmHandled = true;
    window.history.replaceState({}, "", window.location.pathname);
    api.post("/rides/confirm", { token })
      .then(({ data }) => { toast.success(`Corsa confermata e pubblicata • ${data.origin} ➔ ${data.destination}`); refresh(); })
      .catch((e) => toast.error(apiError(e, "Link di conferma non valido")));
  }, [refresh]);
  const openLegal = (section) => {
    setLegalSection(section);
    setPanel("legal");
  };

  const goToBoard = () => {
    document.getElementById("live-board")?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => searchRef.current?.focus({ preventScroll: true }), 500);
  };

  const clearDeepLinks = () => {
    setInviteCode(null);
    setShareCode(null);
    window.history.replaceState({}, "", window.location.pathname);
  };

  const startBooking = (ride, pin = null) => requireLicense(() => setBooking({ ride, pin }));
  const onProfileSaved = () => {
    loadProfile();
    refresh();
  };

  return (
    <div key={lang} className="App min-h-screen bg-black pb-28 md:pb-0" id="top">
      <Header online={online} onOpen={(k) => setPanel(!loggedIn && k !== "legal" ? "auth" : k)} verified={verified} unread={notifs.unread} user={user} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6">
        <Hero stats={stats} online={online} hub={hub} onHub={setHub} service={service} onService={setService} verified={verified || !loggedIn} onCreate={openCreate} onSearch={goToBoard} onProfile={() => setPanel("profile")} vehicle={vehicle} q={q} onQuery={setQ} onVehicle={(v) => { setVehicle(v); document.getElementById("live-board")?.scrollIntoView({ behavior: "smooth", block: "start" }); }} />
        <LiveBoard hub={hub} setHub={setHub} service={service} setService={setService} refreshKey={refreshKey} searchRef={searchRef} verified={verified} vehicle={vehicle} onVehicle={setVehicle} q={q} setQ={setQ} onBook={startBooking} onCreate={openCreate} onUnlock={() => setPanel("auth")} />
      </main>
      <Footer onOpenLegal={openLegal} />
      <StickyCta onClick={openCreate} />
      <InstallBanner />
      <SplashScreen />

      <ExpressRideModal open={panel === "create"} onClose={close} onCreated={refresh} />
      <MyRidesSheet open={panel === "myrides"} onClose={close} refreshKey={refreshKey} />
      <LegalSheet open={panel === "legal"} onClose={close} section={legalSection} onSection={setLegalSection} />
      <ProfileSheet open={panel === "profile"} onClose={close} profile={profile} onSaved={onProfileSaved} onVerifyInfo={() => openLegal("license")} onConcierge={() => requireLicense(() => setPanel("concierge"))} onLogout={() => { logout(); close(); }} />
      <NotificationsSheet open={panel === "notifications"} onClose={close} items={notifs.items} profile={profile} onOpenRide={openRide} onMarkRead={notifs.markAllRead} onProfile={() => setPanel("profile")} />
      <ConciergeSheet open={panel === "concierge"} onClose={close} />
      <AuthDialog open={panel === "auth"} onClose={close} onDone={(isNew) => setPanel(isNew ? "profile" : null)} />
      <AdminSheet open={panel === "admin"} onClose={close} />
      <ResetPasswordDialog token={resetToken} onClose={() => { setResetToken(null); window.history.replaceState({}, "", window.location.pathname); }} onDone={() => setPanel("auth")} />
      <InviteUnlockDialog code={inviteCode} onClose={clearDeepLinks} onBook={startBooking} onUnlock={() => setPanel("auth")} />
      <ShareRideDialog code={shareCode} refreshKey={refreshKey} onClose={clearDeepLinks} onBook={startBooking} onUnlock={() => setPanel("auth")} />
      <BookDialog booking={booking} onClose={() => setBooking(null)} onBooked={() => { refresh(); clearDeepLinks(); }} />
    </div>
  );
}

function App() {
  const conciergeCode = params.get("concierge");
  return (
    <>
      {conciergeCode ? <ConciergeRequestPage code={conciergeCode} /> : <AuthProvider><DriverApp /></AuthProvider>}
      <Toaster theme="dark" position="top-center" richColors />
    </>
  );
}

export default App;
